// src/pages/StaffStats.js
import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { useSchool } from "../hooks/useSchool";
import "../components/StaffStats.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const chartColors = ["#3b82f6","#10b981","#f59e0b","#ef4444","#8b5cf6","#06b6d4"];

const StaffStats = () => {
  const schoolId = useSchool();
  const [tickets, setTickets] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [expandedStaff, setExpandedStaff] = useState(null);

  const [selectedMonths, setSelectedMonths] = useState([new Date().getMonth()]);
  const [selectedStaff, setSelectedStaff] = useState([]);

  // 🔹 Load staff + tickets
  useEffect(() => {
    const loadData = async () => {
      if (!schoolId) return;

      const staffSnap = await getDocs(
        query(collection(db, "users"), where("role", "in", ["staff", "admin"]), where("schoolId", "==", schoolId))
      );

      const staff = staffSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setStaffList(staff);

      // Default: all staff selected
      setSelectedStaff(staff.map(s => s.id));

      const ticketsSnap = await getDocs(
        collection(db, "schools", schoolId, "tickets")
      );
      setTickets(ticketsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    };

    loadData();
  }, [schoolId]);

  // 🔹 Build staff totals by month
  const staffTotals = {};
  tickets.forEach(t => {
    if (!t.givenBy?.uid) return;
    if (t.quantity <= 0) return;

    const giver = t.givenBy.uid;
    const month = t.timestamp?.toDate?.().getMonth();
    if (month === undefined) return;

    if (!staffTotals[giver]) {
      staffTotals[giver] = Array(12).fill(0);
    }
    staffTotals[giver][month] += t.quantity;
  });

  // 🔹 Chart: total selected months for selected staff
  const chartData = {
    labels: staffList
      .filter(s => selectedStaff.includes(s.id))
      .map(s => s.name),
    datasets: [
      {
        label: "Tickets",
        data: staffList
          .filter(s => selectedStaff.includes(s.id))
          .map(s => {
            const arr = staffTotals[s.id] || Array(12).fill(0);
            return selectedMonths.reduce((sum, m) => sum + arr[m], 0);
          }),
        backgroundColor: chartColors,
      },
    ],
  };

  // 🔹 Per staff breakdown (expanded view)
  const renderDetails = (staffId) => {
    const monthly = staffTotals[staffId] || Array(12).fill(0);

    const values = {};
    tickets.forEach(t => {
      if (t.givenBy?.uid !== staffId) return;
      if (t.quantity <= 0) return;
      const type = t.valueType ?? "Other";
      values[type] = (values[type] || 0) + t.quantity;
    });

    return (
      <div className="staff-popup">
        <h4>📊 Breakdown</h4>

        <div className="chart-mini">
          <Bar
            data={{
              labels: monthNames,
              datasets: [{ label: "By Month", data: monthly, backgroundColor: "#3b82f6" }]
            }}
            options={{ responsive: true, plugins: { legend: { display: false } } }}
          />
        </div>

        <div className="chart-mini">
          <Bar
            data={{
              labels: Object.keys(values),
              datasets: [{
                label: "By PBIS Value",
                data: Object.values(values),
                backgroundColor: chartColors
              }]
            }}
            options={{ responsive: true, plugins: { legend: { display: false } } }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard-container">
      <h2 className="section-title">👩‍🏫 Staff Ticket Stats</h2>

      {/* 🔹 Month + Staff Filters */}
      <div className="filters-row">
        <div>
          <h4>Select Months:</h4>
          <div className="month-grid">
            {monthNames.map((m, idx) => (
              <label key={idx}>
                <input
                  type="checkbox"
                  checked={selectedMonths.includes(idx)}
                  onChange={() => {
                    setSelectedMonths(prev =>
                      prev.includes(idx)
                        ? prev.filter(i => i !== idx)
                        : [...prev, idx]
                    );
                  }}
                />
                {m}
              </label>
            ))}
          </div>
        </div>

        <div>
          <h4>Select Staff:</h4>
          <div className="staff-check-grid">
            {staffList.map(s => (
              <label key={s.id}>
                <input
                  type="checkbox"
                  checked={selectedStaff.includes(s.id)}
                  onChange={() => {
                    setSelectedStaff(prev =>
                      prev.includes(s.id)
                        ? prev.filter(i => i !== s.id)
                        : [...prev, s.id]
                    );
                  }}
                />
                {s.name}
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* 🔹 Overview Chart */}
      <div className="section-card">
        <Bar
          data={chartData}
          options={{
            responsive: true,
            plugins: {
              legend: { display: false },
              title: { display: true, text: "Tickets Given (Filtered)" },
            },
          }}
        />
      </div>

      {/* 🔹 Staff List */}
      <div className="staff-grid">
        {staffList.map((s) => (
          <div key={s.id} className="staff-card">
            <div
              className="staff-name"
              onClick={() => setExpandedStaff(expandedStaff === s.id ? null : s.id)}
            >
              {s.name}
            </div>

            {expandedStaff === s.id && renderDetails(s.id)}
          </div>
        ))}
      </div>
    </div>
  );
};

export default StaffStats;